import { Injectable } from '@angular/core';
import { ApiInterface } from '../api-interface';
import { ApiService } from '../api.service';
import { Observable, BehaviorSubject, tap } from 'rxjs';
import {
  ContaDTO,
  ContaResponseDTO,
} from '../../models/gerenciar-contas/conta.model';

@Injectable({
  providedIn: 'root',
})
export class ContaService implements ApiInterface {
  private contaCriadaSubject = new BehaviorSubject<ContaDTO | null>(null);
  public contaCriada$ = this.contaCriadaSubject.asObservable();

  private contasSubject = new BehaviorSubject<ContaDTO[]>([]);
  public contas$ = this.contasSubject.asObservable();

  constructor(private apiService: ApiService) { }

  get<T>(url: string, data?: any): Observable<ContaResponseDTO> {
    const queryParams = this.apiService.buildQueryParams(data);
    const patch = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<ContaResponseDTO>(patch);
  }

  getById<T>(url: string): Observable<ContaDTO> {
    return this.apiService.read<ContaDTO>(url);
  }

  post<T>(url: string, data: ContaDTO): Observable<ContaDTO> {
    return this.apiService.create<ContaDTO>(url, data).pipe(
      tap((contaCriada: ContaDTO) => {
        
        this.contaCriadaSubject.next(contaCriada);
        
        const contasAtuais = this.contasSubject.getValue();
        this.contasSubject.next([...contasAtuais, contaCriada]);
      })
    );
  }

  put<T>(url: string, data: ContaDTO): Observable<ContaDTO> {
    return this.apiService.update<ContaDTO>(url, data);
  }

  delete<T>(url: string): Observable<any> {
    return this.apiService.delete(url);
  }

  /**
   * Busca por título da conta (pesquisa em tempo real).
   * Ajuste o path se no backend for diferente.
   * Exemplo pedido: /titulodaconta?tituloDaConta=REAL
   * Aqui considerei /conta/titulodaconta por consistência com /conta.
   */
  searchByTitulo(tituloDaConta: string, page: number, size: number): Observable<ContaResponseDTO> {
    return this.get('/conta/titulodaconta', { tituloDaConta, page, size });
  }

  /**
   * Lista as contas marcadas para receber pagamento com tarjeta ou QR Code.
   *
   * O filtro é feito no backend. Lista vazia é resposta válida: significa que o
   * usuário ainda não marcou nenhuma conta, e a tela deve orientá-lo a fazer isso.
   */
  listarContasTarjetaQrcode(): Observable<ContaDTO[]> {
    return this.apiService.read<ContaDTO[]>('/conta/tarjeta-qrcode');
  }

  /**
   * Lista as contas de banco em guaranis — as únicas que podem receber o dinheiro cobrado pela
   * Bancard (QR Code e cartão), que cobra em guaranis e deposita num banco.
   *
   * O filtro é feito no backend. Lista vazia é resposta válida: a empresa ainda não tem uma conta
   * assim, e a tela deve orientá-la a cadastrar.
   */
  listarContasBancariasEmGuarani(): Observable<ContaDTO[]> {
    return this.apiService.read<ContaDTO[]>('/conta/banco-guarani');
  }

  /**
   * Lista as contas da PRÓPRIA empresa do contador, ignorando o contexto de
   * cliente ativo (Área do Contador). Usa `readSemContextoContador`, que marca a
   * request para o `ContadorContextoInterceptor` NÃO injetar `X-Empresa-Contexto`
   * — sem isso, o backend devolveria as contas do CLIENTE, não as do contador.
   */
  listarContasDaPropriaEmpresa(size = 500): Observable<ContaResponseDTO> {
    const queryParams = this.apiService.buildQueryParams({ size });
    const path = queryParams ? `/conta?${queryParams}` : '/conta';
    return this.apiService.readSemContextoContador<ContaResponseDTO>(path);
  }
}
